import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CheckCircle2, Circle, TrendingUp, ArrowRight } from 'lucide-react';
import { useIntl } from 'react-intl';

/**
 * Labels for the eight section keys in `Profile::SectionCompletion::DISPLAY_ORDER`.
 *
 * The set and order are product-owned and intentionally stable — the server sends
 * them in order and this component renders them as received. `defaultMessage` is
 * mandatory on each: without it a missing translation renders the raw key id to the
 * user, a bug this very page ships elsewhere (the headline shows
 * `sign_up_pages.headline.exploring.one`).
 */
export const SECTION_LABELS = {
  header: { id: 'succession.profile_edit.progress_banner.section_header', defaultMessage: 'Header' },
  goals: { id: 'succession.profile_edit.progress_banner.section_goals', defaultMessage: 'Goals' },
  bio: { id: 'succession.profile_edit.progress_banner.section_bio', defaultMessage: 'Bio' },
  revenue: { id: 'succession.profile_edit.progress_banner.section_revenue', defaultMessage: 'Revenue' },
  practice: { id: 'succession.profile_edit.progress_banner.section_practice', defaultMessage: 'Practice' },
  professional: {
    id: 'succession.profile_edit.progress_banner.section_professional',
    defaultMessage: 'Professional',
  },
  personal: { id: 'succession.profile_edit.progress_banner.section_personal', defaultMessage: 'Personal' },
  credentials: { id: 'succession.profile_edit.progress_banner.section_credentials', defaultMessage: 'Credentials' },
};

const getProgressColor = (percentage) => {
  if (percentage >= 80) return { ring: '#22c55e', glow: 'rgba(34, 197, 94, 0.15)' }; // green
  if (percentage >= 50) return { ring: '#3b82f6', glow: 'rgba(59, 130, 246, 0.15)' }; // blue
  if (percentage >= 25) return { ring: '#f59e0b', glow: 'rgba(245, 158, 11, 0.15)' }; // amber
  return { ring: '#ef4444', glow: 'rgba(239, 68, 68, 0.15)' }; // red
};

/**
 * Animated circular progress ring using SVG.
 */
const CircularProgress = ({ percentage, size = 80, strokeWidth = 6, locale = 'en' }) => {
  const [animatedPct, setAnimatedPct] = useState(0);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (animatedPct / 100) * circumference;
  const colors = getProgressColor(percentage);

  useEffect(() => {
    const timer = setTimeout(() => setAnimatedPct(percentage), 100);
    return () => clearTimeout(timer);
  }, [percentage]);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* Glow effect */}
      <div
        className="absolute inset-0 rounded-full transition-all duration-700"
        style={{ boxShadow: `0 0 20px 4px ${colors.glow}` }}
      />
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-succession-bg-secondary"
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={colors.ring}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      {/* Percentage label */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-bold text-succession-text-primary">
          {Math.round(animatedPct)}{locale === 'fr' ? ' %' : '%'}
        </span>
      </div>
    </div>
  );
};

/**
 * A slim section-progress dot indicator. Completion is a server-provided boolean,
 * so there are exactly two states — no partial.
 */
const SectionDot = ({ section, label, onClick }) => {
  const Icon = section.complete ? CheckCircle2 : Circle;
  const colorClass = section.complete ? 'text-succession-success' : 'text-succession-text-muted';

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1 group cursor-pointer"
      data-section-key={section.key}
      title={label}
    >
      <Icon className={`w-5 h-5 ${colorClass} transition-transform group-hover:scale-125`} />
      <span className="text-[10px] font-medium text-succession-text-muted group-hover:text-succession-text-primary transition-colors whitespace-nowrap">
        {label}
      </span>
    </button>
  );
};

/**
 * Segmented progress bar with section dots aligned beneath each segment. Segments
 * are equal width: completion is now boolean per section, so there is no field
 * count to weight them by.
 */
const SegmentedProgressWithDots = ({ sections, labelFor, onSectionClick }) => (
  <div className="flex w-full">
    {sections.map((section, idx) => (
      <div
        key={section.key}
        className="flex flex-col items-center"
        style={{ width: `${100 / sections.length}%` }}
      >
        {/* Bar segment */}
        <div
          className="relative h-2 w-full bg-succession-bg-secondary"
          style={{
            borderRadius:
              idx === 0
                ? '9999px 0 0 9999px'
                : idx === sections.length - 1
                  ? '0 9999px 9999px 0'
                  : '0',
          }}
        >
          <div
            className="absolute inset-y-0 left-0 transition-all duration-700 ease-out"
            style={{
              width: section.complete ? '100%' : '0%',
              borderRadius:
                idx === 0
                  ? '9999px 0 0 9999px'
                  : idx === sections.length - 1
                    ? '0 9999px 9999px 0'
                    : '0',
              backgroundColor: 'var(--succession-success, #22c55e)',
            }}
          />
          {/* Section divider */}
          {idx < sections.length - 1 && (
            <div className="absolute right-0 inset-y-0 w-px bg-succession-bg-primary" />
          )}
        </div>
        {/* Dot centered beneath this segment */}
        <div className="mt-2">
          <SectionDot
            section={section}
            label={labelFor(section)}
            onClick={() => onSectionClick(section.key)}
          />
        </div>
      </div>
    ))}
  </div>
);

/**
 * ProfileProgressBanner — displayed at the top of the succession profile edit page.
 *
 * Both the ring and the dots come from `profile_section_completion` /
 * `profile_section_percentage`, computed by `Profile::SectionCompletion`. This
 * component deliberately contains NO completion logic: it previously kept its own
 * field lists, which disagreed with the server and made Goals and Revenue
 * impossible to complete. Do not reintroduce a client-side prediction, and do not
 * fall back to `profile_percentage_complete` for the ring — that value has an
 * unreachable ceiling on this page.
 */
const ProfileProgressBanner = ({ user }) => {
  const intl = useIntl();

  const labelFor = (section) => {
    const label = SECTION_LABELS[section.key];
    if (label) return intl.formatMessage(label);
    // Unknown key from a newer server: show something human rather than a raw id.
    return section.key.replace(/_/g, ' ');
  };

  const getMotivationText = (percentage) => {
    if (percentage >= 100) return intl.formatMessage({ id: 'succession.profile_edit.progress_banner.motivation_100' });
    if (percentage >= 80) return intl.formatMessage({ id: 'succession.profile_edit.progress_banner.motivation_80' });
    if (percentage >= 60) return intl.formatMessage({ id: 'succession.profile_edit.progress_banner.motivation_60' });
    if (percentage >= 40) return intl.formatMessage({ id: 'succession.profile_edit.progress_banner.motivation_40' });
    if (percentage >= 20) return intl.formatMessage({ id: 'succession.profile_edit.progress_banner.motivation_20' });
    return intl.formatMessage({ id: 'succession.profile_edit.progress_banner.motivation_0' });
  };

  // Only sections the user can actually finish here get a dot, so 100% is
  // reachable. The server already excludes unreachable-and-incomplete sections.
  const sections = (user.profile_section_completion || []).filter((s) => s.countable);
  const completedCount = sections.filter((s) => s.complete).length;
  const percentage = user.profile_section_percentage ?? 0;
  const motivationText = getMotivationText(percentage);

  // Scroll by stable attribute rather than DOM index. The previous implementation
  // indexed `.space-y-6 > *`, whose first child is AvatarUpdateBanner — a
  // conditionally rendered element — so every dot scrolled to the wrong section
  // whenever that banner was visible.
  // `~=` matches one key within a space-separated list, so a single card can host
  // several sections (the header card owns both `headline` and `photo`). Sections
  // with no input on the page have no anchor; the null check makes that a no-op
  // rather than a crash.
  const scrollToSection = (sectionKey) => {
    const target = document.querySelector(`[data-profile-section~="${sectionKey}"]`);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const firstIncomplete = sections.find((s) => !s.complete);

  return (
    <div className="bg-succession-bg-secondary border border-succession-border rounded-xl p-5 mb-8 transition-all duration-300 hover:shadow-succession-md">
      <div className="flex items-center gap-6">
        {/* Circular Progress Ring */}
        <div className="flex-shrink-0">
          <CircularProgress percentage={percentage} size={80} strokeWidth={6} locale={intl.locale} />
        </div>

        {/* Text & Section Indicators */}
        <div className="flex-1 min-w-0">
          {/* Top row: motivation + section count */}
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h2 className="text-sm font-semibold text-succession-text-primary mb-0.5">
                {intl.formatMessage({ id: 'succession.profile_edit.progress_banner.profile_completion' })}
              </h2>
              <p className="text-sm text-succession-text-secondary leading-snug">
                {motivationText}
              </p>
            </div>
            {sections.length > 0 && (
              <div className="flex items-center gap-1.5 text-succession-text-muted text-xs font-medium whitespace-nowrap flex-shrink-0 mt-0.5">
                <TrendingUp className="w-3.5 h-3.5" />
                <span>
                  {intl.formatMessage(
                    { id: 'succession.profile_edit.progress_banner.sections_count' },
                    { completed: completedCount, total: sections.length },
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Segmented progress bar with aligned section dots */}
          {sections.length > 0 && (
            <SegmentedProgressWithDots
              sections={sections}
              labelFor={labelFor}
              onSectionClick={scrollToSection}
            />
          )}
        </div>
      </div>

      {/* CTA for low completion */}
      {percentage < 50 && firstIncomplete && (
        <div className="mt-4 pt-3 border-t border-succession-border">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection(firstIncomplete.key);
            }}
            className="inline-flex items-center text-sm font-medium text-succession-primary hover:text-succession-primary-hover transition-colors"
          >
            {intl.formatMessage(
              { id: 'succession.profile_edit.progress_banner.start_with' },
              { label: labelFor(firstIncomplete) },
            )}
            <ArrowRight className="w-4 h-4 ml-1" />
          </a>
        </div>
      )}
    </div>
  );
};

ProfileProgressBanner.propTypes = {
  user: PropTypes.shape({
    profile_section_percentage: PropTypes.number,
    profile_section_completion: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        complete: PropTypes.bool,
        countable: PropTypes.bool,
        reason_not_countable: PropTypes.string,
      }),
    ),
  }).isRequired,
};

export default ProfileProgressBanner;
